import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { SceneEditor } from '@zakeke/zakeke-3d-core';
import { useLazyLoadQuery } from 'react-relay';
import styled from 'styled-components';
import { AiHeading, AiRotatingThinking, AiSupportCard, AiUtilityHandoff, VoiceInputButton, useSpeechRecognition } from 'src/components/shared/aiAssistant';
import { BasicButton } from 'src/components/widgets/buttons';
import { Checkbox } from 'src/components/widgets/checkbox';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { Dialog } from 'src/components/widgets/dialog';
import { FormControl } from 'src/components/widgets/formControl';
import { Icon } from 'src/components/widgets/icon';
import Loader from 'src/components/widgets/loader';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';
import Stack from 'src/components/widgets/stack/stack';
import { TextArea } from 'src/components/widgets/textArea';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import ExpandLessIcon from 'src/shared/material_icons/navigation/expand_less.svg';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import {
    AttributePreviewAction,
    AttributePreviewItems,
    AttributePreviewOption,
    ComposerAttributeNode
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { composerQueriesCheckAiLimitQuery } from '../__generated__/composerQueriesCheckAiLimitQuery.graphql';
import { AiGenerationInput, composerQueriesGenerateAiAttributesQuery } from '../__generated__/composerQueriesGenerateAiAttributesQuery.graphql';
import { composerCheckAiLimitQuery, composerGenerateAiAttributes } from '../composerQueries';
import { getCapitalizedFormattedName } from '../helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../helpers/composerNodesAdapter';

interface ComposerGenerateAIAttributeDialogProps {
    meshList?: BABYLON.AbstractMesh[];
    materialList?: BABYLON.Material[];
    currentAttributes?: ComposerAttributeNode[];
    onConfirm: (attributes: ComposerAttributeNode[]) => void;
    onClose: () => void;
    sceneEditor: SceneEditor | null | undefined;
}

type GenerationResult = composerQueriesGenerateAiAttributesQuery['response']['generatedAIAttributes'];
type GenerationRequest = { id: number; input: AiGenerationInput };
interface GenerationCatalogItem {
    id: string;
    name: string;
}

const getMetadataDescription = (metadata: unknown): string | null => {
    if (!metadata || typeof metadata !== 'object') return null;
    const description = (metadata as Record<string, unknown>).description;
    return typeof description === 'string' && description.trim() ? description.trim() : null;
};

const getMetadataTags = (metadata: unknown): string[] => {
    if (!metadata || typeof metadata !== 'object') return [];
    const metadataRecord = metadata as Record<string, unknown>;
    const tags = metadataRecord.tags ?? metadataRecord.keywords;
    if (Array.isArray(tags)) return tags.filter((tag): tag is string => typeof tag === 'string' && !!tag.trim()).map((tag) => tag.trim());
    if (typeof tags === 'string')
        return tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
    return [];
};

const getMeshPath = (mesh: BABYLON.AbstractMesh): string => {
    const path: string[] = [];
    let current: BABYLON.Node | null = mesh;
    while (current) {
        if (current.name) path.unshift(current.name);
        current = current.parent;
    }
    return path.join(' / ');
};

const resolveCatalogItem = <T extends GenerationCatalogItem>(value: string | null | undefined, catalog: ReadonlyArray<T>): T | undefined => {
    const normalizedValue = value?.trim();
    if (!normalizedValue) return undefined;

    const exactMatch = catalog.find((item) => item.id === normalizedValue || item.name === normalizedValue);
    if (exactMatch) return exactMatch;

    const prefixMatches = catalog.filter((item) => item.id.startsWith(normalizedValue));
    return prefixMatches.length === 1 ? prefixMatches[0] : undefined;
};

const normalizeActionType = (value: string | null | undefined) => value?.trim().replace(/ /g, '_').toLowerCase() ?? '';

const PreviewLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
    gap: 24px;
    min-height: 420px;
    @media (max-width: 800px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;
const AttributeList = styled(Stack)`
    max-height: 480px;
    overflow-y: auto;
    min-width: 0;
`;
const Viewer = styled.div`
    position: relative;
    min-width: 0;
    min-height: 420px;
    border-radius: 12px;
    overflow: hidden;
    background: ${theme.app.backgroundColor};
`;
const AttributeCard = styled.div<{ $selected: boolean }>`
    border: 1px solid ${(props) => (props.$selected ? theme.app.secondary : theme.app.borderColor)};
    border-radius: 8px;
    overflow: hidden;
`;
const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
    background: ${theme.app.backgroundColor};
`;
const OptionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;
const ActionText = styled.div`
    color: ${theme.app.textColor};
    font-size: 12px;
    overflow-wrap: anywhere;
`;
const CardBody = styled(Stack)`
    padding: 12px;
`;
const Centered = styled(Stack)`
    min-height: 180px;
    justify-content: center;
    align-items: center;
`;

export function AttributePreviewCard({
    attributeTitle,
    selected,
    optionsList,
    onOptionClick,
    onSelected
}: {
    attributeTitle: string;
    selected: boolean;
    optionsList: AttributePreviewOption[];
    onOptionClick: (option: AttributePreviewOption) => Promise<void>;
    onSelected: (selected: boolean) => void;
}) {
    const [open, setOpen] = useState(true);
    const [expanded, setExpanded] = useState<number[]>([]);
    const [playing, setPlaying] = useState(false);
    const actionText = (action: AttributePreviewAction) =>
        `${action.type}: ${(action.objectLabels ?? action.objects).join(', ')}${action.material ? ` → ${action.materialLabel ?? action.material}` : ''}`;
    return (
        <AttributeCard $selected={selected}>
            <CardHeader>
                <BasicButton type='button' onClick={() => setOpen(!open)} aria-expanded={open}>
                    <Icon>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Icon>
                    {attributeTitle}
                </BasicButton>
                <Checkbox checked={selected} onChange={onSelected} label={T._('Import', 'Admin')} />
            </CardHeader>
            {open && (
                <CardBody $gap='12px'>
                    {optionsList.map((option, index) => (
                        <Stack key={index} $gap='6px'>
                            <OptionRow>
                                <BasicButton
                                    type='button'
                                    aria-expanded={expanded.includes(index)}
                                    onClick={() => setExpanded(expanded.includes(index) ? expanded.filter((item) => item !== index) : [...expanded, index])}
                                >
                                    <Icon>{expanded.includes(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Icon>
                                    {option.name}
                                </BasicButton>
                                <BasicButton
                                    type='button'
                                    $size='small'
                                    disabled={playing}
                                    onClick={async () => {
                                        setPlaying(true);
                                        try {
                                            await onOptionClick(option);
                                        } finally {
                                            setPlaying(false);
                                        }
                                    }}
                                    aria-label={`${T._('Preview', 'Admin')}: ${option.name}`}
                                >
                                    <Icon>
                                        <PlayIcon />
                                    </Icon>
                                    {T._('Preview', 'Admin')}
                                </BasicButton>
                            </OptionRow>
                            {expanded.includes(index) &&
                                option.actions.map((action, actionIndex) => <ActionText key={actionIndex}>{actionText(action)}</ActionText>)}
                        </Stack>
                    ))}
                </CardBody>
            )}
        </AttributeCard>
    );
}

// Move the existing viewer into the preview and return it to the Composer on every exit.
const ScenePreview: React.FC<{ sceneEditor: SceneEditor | null | undefined }> = ({ sceneEditor }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!sceneEditor || !ref.current) return;
        const originalParent = sceneEditor.canvas?.parentElement?.parentElement;
        const meshes = (sceneEditor.scene?.meshes ?? []).map((mesh) => ({
            mesh,
            material: mesh.material,
            isVisible: mesh.isVisible,
            enabled: mesh.isEnabled()
        }));
        const colours = (sceneEditor.scene?.materials ?? []).map((material) => {
            const colour = (material as BABYLON.PBRMaterial).albedoColor ?? (material as BABYLON.StandardMaterial).diffuseColor;
            return { colour, original: colour?.clone() };
        });
        sceneEditor.appendTo(ref.current);
        sceneEditor.updateView(false, true);
        return () => {
            // Trying an option must not leave temporary changes in the product scene.
            meshes.forEach(({ mesh, material, isVisible, enabled }) => {
                mesh.material = material;
                mesh.isVisible = isVisible;
                mesh.setEnabled(enabled);
            });
            colours.forEach(({ colour, original }) => {
                if (colour && original) colour.copyFrom(original);
            });
            if (originalParent?.isConnected) {
                sceneEditor.appendTo(originalParent);
                sceneEditor.updateView(false, true);
            }
        };
    }, [sceneEditor]);
    return <Viewer ref={ref}>{!sceneEditor && T._('3D preview is not available.', 'Admin')}</Viewer>;
};

const GenerateRequest: React.FC<{ request: GenerationRequest; onGenerated: (result: GenerationResult, input: AiGenerationInput) => void }> = ({
    request,
    onGenerated
}) => {
    const result = useLazyLoadQuery<composerQueriesGenerateAiAttributesQuery>(
        composerGenerateAiAttributes,
        { input: request.input },
        { fetchPolicy: 'network-only', fetchKey: request.id }
    );
    const delivered = useRef(false);
    useEffect(() => {
        if (delivered.current) return;
        delivered.current = true;
        onGenerated(result.generatedAIAttributes, request.input);
    }, [result, request, onGenerated]);
    if (!result.generatedAIAttributes) throw new Error('AI generation did not return a result');
    return <Thinking />;
};
const Thinking = () => (
    <Centered>
        <AiRotatingThinking
            phrases={[T._('Reading your prompt...', 'Admin'), T._('Generating attributes and options...', 'Admin'), T._('Almost ready...', 'Admin')]}
        />
    </Centered>
);

const mapGeneratedAttributes = (result: GenerationResult, input: AiGenerationInput): AttributePreviewItems[] => {
    const materialsList = input.materials;
    const meshesList = input.meshes;
    let attributes: AttributePreviewItems[] =
        result.attributes?.map((attr) => ({
            name: attr?.an ?? '',
            selected: true,
            options:
                attr?.o?.map((option) => ({
                    name: option?.on ?? '',
                    actions:
                        option?.a?.map((action) => {
                            const actionType = normalizeActionType(action?.t);
                            const targetCatalog = actionType === 'set_material_color' ? materialsList : meshesList;
                            const targetItems = (action?.m ?? []).map((target) => resolveCatalogItem(target, targetCatalog));
                            const materialItem = actionType === 'set_object_material' ? resolveCatalogItem(action?.mat, materialsList) : undefined;

                            return {
                                material: materialItem?.id ?? action?.mat ?? '',
                                materialLabel: materialItem?.name,
                                type: getCapitalizedFormattedName(action?.t ?? ''),
                                objects: targetItems.map((item, index) => item?.id ?? action?.m?.[index] ?? ''),
                                objectLabels: targetItems.map((item, index) => item?.name ?? action?.m?.[index] ?? '')
                            };
                        }) ?? []
                })) ?? []
        })) ?? [];

    attributes.forEach((attr) => {
        attr.options = attr.options.filter((option) => {
            option.actions = option.actions.filter((action) => {
                const actionType = normalizeActionType(action.type);
                const targetCatalog = actionType === 'set_material_color' ? materialsList : meshesList;
                const targetsAreValid = action.objects.length > 0 && action.objects.every((target) => resolveCatalogItem(target, targetCatalog) !== undefined);

                if (!targetsAreValid) return false;
                if (actionType === 'show_object' || actionType === 'hide_object') return true;
                if (actionType === 'set_object_material') return resolveCatalogItem(action.material, materialsList) !== undefined;
                if (actionType === 'set_material_color') return /^#[0-9A-Fa-f]{6}$/.test(action.material);
                return false;
            });
            return option.actions.length > 0;
        });
    });
    attributes = attributes.filter((attr) => attr.name.trim() !== '' && attr.options.length > 0);

    return attributes;
};

const ComposerGenerateAIAttributeDialog: React.FC<ComposerGenerateAIAttributeDialogProps> = ({
    meshList,
    materialList,
    currentAttributes,
    onConfirm,
    onClose,
    sceneEditor
}) => {
    // Unlike the old REST status check, GraphQL returns true when generation is allowed.
    const quota = useLazyLoadQuery<composerQueriesCheckAiLimitQuery>(composerCheckAiLimitQuery, {}, { fetchPolicy: 'network-only' });
    const validation = useValidationContext();
    const [prompt, setPrompt] = useState('');
    const [attributes, setAttributes] = useState<AttributePreviewItems[]>([]);
    const [request, setRequest] = useState<GenerationRequest | null>(null);
    const requestID = useRef(0);
    const [route, setRoute] = useState<GenerationResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const speech = useSpeechRecognition({
        disabled: !!request || attributes.length > 0 || !quota.checkAILimited,
        onTranscript: (text) => setPrompt((value) => [value, text].filter(Boolean).join(' ')),
        onError: () => setError(T._('Could not access your microphone. You can type your prompt instead.', 'Admin'))
    });
    const onGenerated = useCallback((result: GenerationResult, input: AiGenerationInput) => {
        setRequest(null);
        if (result?.supportRequest || result?.outOfScope) {
            setRoute(result);
            if (!result.supportRequest && !result.handoffPrompt)
                setError(T._('Please describe the attributes and options you want to create for this product.', 'Admin'));
            return;
        }
        setRoute(null);
        const generated = mapGeneratedAttributes(result, input);
        if (!generated.length) {
            setError(T._('No valid attributes generated. Please try a different prompt.', 'Admin'));
            return;
        }
        setAttributes(generated);
    }, []);
    const generate = () => {
        if (!validation.submit() || !prompt.trim() || request || !quota.checkAILimited) return;
        speech.stopListening();
        setError(null);
        setRoute(null);
        setRequest({
            id: ++requestID.current,
            input: {
                prompt: prompt.trim(),
                materials: (materialList ?? [])
                    .filter((item) => item.name && !['default material', 'skybox', 'ground'].some((name) => item.name.toLowerCase().includes(name)))
                    .map((item) => ({
                        id: item.id,
                        name: item.name,
                        description: getMetadataDescription(item.metadata),
                        tags: getMetadataTags(item.metadata)
                    })),
                meshes: (meshList ?? [])
                    .filter((item) => item.name && !['vto_reference', 'transforms'].some((name) => item.name.toLowerCase().includes(name)))
                    .map((item) => ({
                        id: item.id,
                        name: item.name,
                        parentID: item.parent?.id ?? null,
                        description: getMetadataDescription(item.metadata),
                        tags: getMetadataTags(item.metadata),
                        path: getMeshPath(item),
                        materialID: item.material?.id ?? null,
                        isVisible: item.isVisible && item.isEnabled()
                    })),
                currentAttributes: (currentAttributes ?? []).map((attribute) => ({
                    id: attribute.id,
                    name: attribute.name,
                    type: attribute.type,
                    description: attribute.description || null,
                    parentID: attribute.parentID ?? null,
                    options: attribute.options.map((option) => ({ id: option.id, name: option.name, description: option.description || null }))
                }))
            }
        });
    };
    const executeAction = async (option: AttributePreviewOption) => {
        if (!sceneEditor?.scene) return;
        try {
            // Preview the same actions that will be imported and serialized on confirmation.
            const actions = option.actions.map((action, index) =>
                ComposerAttributeNodeAdapter.fromActionNode({
                    ...ComposerAttributeNodeAdapter.toNodeActionFromAiAttribute('', '', action),
                    displayOrder: index
                })
            );
            await sceneEditor.executeActionsAsync(null, null, actions);
        } catch {
            showErrorNotification({ message: T._('Could not preview this option. Please try again.', 'Admin') });
        }
    };

    const preview = attributes.length > 0;
    const title = preview ? T._('AI Generated Attributes Preview', 'Admin') : T._('Generate attributes and options with AI', 'Admin');
    const tryAgain = () => {
        setAttributes([]);
        setRequest(null);
        setError(null);
    };
    return (
        <Dialog
            size={preview ? 'large' : 'medium'}
            titleHtml={<AiHeading caption={title} />}
            onClose={onClose}
            buttons={[
                { label: T._('Cancel', 'Admin'), outline: true, onClick: onClose },
                ...(preview
                    ? [
                          { label: T._('Try Again', 'Admin'), outline: true, onClick: tryAgain },
                          {
                              label: T._('Confirm', 'Admin'),
                              disabled: !attributes.some((attribute) => attribute.selected),
                              onClick: () =>
                                  onConfirm(attributes.filter((attribute) => attribute.selected).map(ComposerAttributeNodeAdapter.toNodeFromAiAttribute))
                          }
                      ]
                    : request || !quota.checkAILimited
                      ? []
                      : [{ label: T._('Generate', 'Admin'), disabled: !prompt.trim() || speech.isListening, onClick: generate }])
            ]}
        >
            <Stack>
                {!quota.checkAILimited ? (
                    <NotificationBanner
                        type='CRITICAL'
                        title={T._('AI Limit Reached', 'Admin')}
                        content={T._('AI generation is currently unavailable for your account. Please check your usage limits or contact support.', 'Admin')}
                    />
                ) : request ? (
                    <CustomErrorBoundary
                        key={request.id}
                        render={() => (
                            <Stack>
                                <NotificationBanner
                                    type='CRITICAL'
                                    title={T._('Generation failed', 'Admin')}
                                    content={T._('Could not generate attributes. Please try again or check your prompt, materials and meshes.', 'Admin')}
                                />
                                <BasicButton onClick={() => setRequest(null)}>{T._('Try Again', 'Admin')}</BasicButton>
                            </Stack>
                        )}
                    >
                        <Suspense fallback={<Thinking />}>
                            <GenerateRequest request={request} onGenerated={onGenerated} />
                        </Suspense>
                    </CustomErrorBoundary>
                ) : preview ? (
                    <>
                        <p>{T._('Choose the attributes to import. Preview each option on the model before confirming.', 'Admin')}</p>
                        <PreviewLayout>
                            <AttributeList $gap='12px'>
                                {attributes.map((attribute, index) => (
                                    <AttributePreviewCard
                                        key={index}
                                        attributeTitle={attribute.name}
                                        selected={attribute.selected}
                                        optionsList={attribute.options}
                                        onOptionClick={executeAction}
                                        onSelected={(selected) =>
                                            setAttributes((items) => items.map((item, itemIndex) => (itemIndex === index ? { ...item, selected } : item)))
                                        }
                                    />
                                ))}
                            </AttributeList>
                            <ScenePreview sceneEditor={sceneEditor} />
                        </PreviewLayout>
                    </>
                ) : (
                    <>
                        {error && <NotificationBanner type='CRITICAL' content={error} />}
                        {route?.supportRequest && <AiSupportCard request={route.supportRequest} />}
                        {route?.handoffPrompt && <AiUtilityHandoff prompt={route.handoffPrompt} onClose={onClose} />}
                        <FormControl label={T._('Describe the attributes and options you want to create', 'Admin')}>
                            <TextArea
                                aria-label={T._('Prompt', 'Admin')}
                                rows={7}
                                value={prompt}
                                onChange={(event) => setPrompt(event.target.value)}
                                placeholder={T._('For example: a chair with or without armrests and a choice of wood or metal legs.', 'Admin')}
                                validationKey='composerAIPrompt'
                                validate={validateRequiredTrimmed}
                                validationMessage={T._('Prompt is required.', 'Admin')}
                            />
                            {speech.isSupported && <VoiceInputButton isListening={speech.isListening} onClick={speech.toggleListening} />}
                        </FormControl>
                        <NotificationBanner
                            type='INFO'
                            title={T._('Tips for better results', 'Admin')}
                            content={T._(
                                'Use descriptive names for meshes and materials, and explain which parts, colours or finishes the buyer should choose. You can review the generated attributes before adding them to your product.',
                                'Admin'
                            )}
                        />
                    </>
                )}
            </Stack>
        </Dialog>
    );
};
const ComposerGenerateAIAttributeDialogWithValidation: React.FC<ComposerGenerateAIAttributeDialogProps> = (props) => {
    const [attempt, setAttempt] = useState(0);
    return (
        <ValidationStateProvider>
            <CustomErrorBoundary
                resetKey={attempt}
                render={() => (
                    <Dialog
                        title={T._('Generate attributes and options with AI', 'Admin')}
                        onClose={props.onClose}
                        buttons={[
                            { label: T._('Cancel', 'Admin'), outline: true, onClick: props.onClose },
                            { label: T._('Try Again', 'Admin'), onClick: () => setAttempt((value) => value + 1) }
                        ]}
                    >
                        <NotificationBanner type='CRITICAL' content={T._('Could not check AI availability. Please try again.', 'Admin')} />
                    </Dialog>
                )}
            >
                <Suspense
                    fallback={
                        <Dialog titleHtml={<AiHeading caption={T._('Generate attributes and options with AI', 'Admin')} />} onClose={props.onClose}>
                            <Centered>
                                <Loader />
                            </Centered>
                        </Dialog>
                    }
                >
                    <ComposerGenerateAIAttributeDialog key={attempt} {...props} />
                </Suspense>
            </CustomErrorBoundary>
        </ValidationStateProvider>
    );
};
export default ComposerGenerateAIAttributeDialogWithValidation;
